"use client";

import { useState } from "react";
import { inputClass, labelClass } from "@/components/form";
import { Segmented } from "@/components/Segmented";
import { StampTool } from "@/components/StampTool";
import { outputName } from "@/lib/files";
import { isWinAnsi } from "@/lib/pdf/items";
import {
  DEFAULT_WATERMARK,
  type WatermarkOptions,
} from "@/lib/pdf/stamp-options";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";
import { watermarkFile } from "@/workers/pdf-client";

const tool = getTool("watermark-pdf")!;

const COLORS = [
  { value: "#c8203f", label: "Red" },
  { value: "#545a72", label: "Grey" },
  { value: "#2357d8", label: "Blue" },
  { value: "#0f1222", label: "Black" },
];

/** Watermark (Phase 5, browser): see-through text on every page. */
export function WatermarkTool() {
  const [o, setO] = useState<WatermarkOptions>(DEFAULT_WATERMARK);
  const set = (patch: Partial<WatermarkOptions>) =>
    setO((old) => ({ ...old, ...patch }));
  const text = o.text.trim();
  const writable = isWinAnsi(text);

  return (
    <StampTool
      tool={tool}
      button="Add watermark"
      busyLabel="Adding the watermark…"
      ready={text !== "" && writable}
      run={(file) => watermarkFile(file, o)}
      resultName={(file) => outputName(file.name, "watermarked", "pdf")}
      overlay={({ size }) => {
        const mark = (key: number) => (
          <span
            key={key}
            className="font-bold whitespace-nowrap"
            style={{
              fontSize: `${(o.size / size.width) * 100}cqw`,
              color: o.color,
              opacity: o.opacity,
              transform: `rotate(${-o.angle}deg)`,
              fontFamily: "Helvetica, Arial, sans-serif",
            }}
          >
            {text}
          </span>
        );
        return (
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-0 overflow-hidden",
              o.layout === "center"
                ? "flex items-center justify-center"
                : "grid grid-cols-2 place-items-center",
            )}
          >
            {o.layout === "center"
              ? mark(0)
              : Array.from({ length: 6 }, (_, i) => mark(i))}
          </div>
        );
      }}
    >
      <label className={labelClass}>
        Watermark text
        <input
          className={inputClass}
          value={o.text}
          maxLength={40}
          onChange={(e) => set({ text: e.target.value })}
        />
      </label>
      {!writable && (
        <p className="text-sm text-ink">
          Watermarks can use letters, numbers and common symbols from Western
          languages only.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Segmented
          label="Placement"
          options={[
            { value: "center", label: "Middle" },
            { value: "tiled", label: "Repeat" },
          ]}
          value={o.layout}
          onChange={(layout) => set({ layout })}
          stretch
        />
        <Segmented
          label="Direction"
          options={[
            { value: "45", label: "Diagonal" },
            { value: "0", label: "Straight" },
          ]}
          value={String(o.angle) as "45" | "0"}
          onChange={(angle) => set({ angle: Number(angle) as 0 | 45 })}
          stretch
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className={labelClass}>
          <label htmlFor="wm-size" className="flex justify-between">
            Size <span>{o.size} pt</span>
          </label>
          <input
            id="wm-size"
            type="range"
            min={20}
            max={120}
            value={o.size}
            onChange={(e) => set({ size: Number(e.target.value) })}
            className="h-11 w-full accent-brand"
          />
        </div>
        <div className={labelClass}>
          <label htmlFor="wm-opacity" className="flex justify-between">
            Opacity <span>{Math.round(o.opacity * 100)}%</span>
          </label>
          <input
            id="wm-opacity"
            type="range"
            min={10}
            max={80}
            step={5}
            value={Math.round(o.opacity * 100)}
            onChange={(e) => set({ opacity: Number(e.target.value) / 100 })}
            className="h-11 w-full accent-brand"
          />
        </div>
      </div>
      <div role="group" aria-label="Colour" className="flex flex-wrap gap-1">
        {COLORS.map((c) => (
          <button
            key={c.value}
            type="button"
            aria-label={c.label}
            aria-pressed={o.color === c.value}
            onClick={() => set({ color: c.value })}
            className={cn(
              "flex size-11 items-center justify-center rounded-full border-2",
              o.color === c.value ? "border-brand" : "border-transparent",
            )}
          >
            <span
              className="size-7 rounded-full"
              style={{ background: c.value }}
            />
          </button>
        ))}
      </div>
    </StampTool>
  );
}
